//? Note: jurisdiction specific component

import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import TagManager from 'react-gtm-module';
import { Helmet } from 'react-helmet';
import { camelCase } from 'lodash';
import {
    IfNotMobile,
    CMSContent,
    StyleOverrides,
    scrollToHashElement,
    ErrorBoundary,
    TeleScript,
    getCMSObject,
    getInterpolatedPageData,
    FeatureLockout,
    DisplayContent,
    TitleBreadcrumb,
    HeroBanner,
    PointsDashboard,
} from 'sg-ui-components';

import { Header } from './Layouts/Header';
import Footer from './Layouts/Footer';
import IdleTimeout from './IdleTimeout';
import LoadingIndicator from './LoadingIndicator';
import Menu from './Menu';

import siteConfig from '../promotionConfig';

import UserLockout from '../utils/user_lockout';
import LoggedInOnlyPages from '../Components/LoggedInOnlyPages';
import { mapStateToProps, mapDispatchToProps } from '../Store';
import RefreshPageForGuests from './RefreshPageForGuests';
import RenewExpiredSession from './RenewExpiredSession';

const Layout = ({ session, user, config, cmsSourceFirebase, actions, children, match, configReplacements, banners }) => {
    const path = match?.path ?? '';
    const pageData = getInterpolatedPageData(config?.site?.routes, match, configReplacements);
    const errorBoundaryFallback = getCMSObject('data.messages.pageErrorMessages.jsonBlock')?.[`${camelCase(path) || 'home'}Fallback`];
    const telescriptPointsDashboard = getCMSObject('data.components.teleScripts.pointsDashboard.jsonBlock');

    //? Note: Session will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function sessionStateActions() {
            await actions.sessionActions.getSession();
        }

        if (!session?.ready) {
            sessionStateActions();
            console.log('🟢 Initialization: Establish session');
        }
    }, []);

    //? Note: Frontend config will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function configStateActions() {
            await actions.configActions.getEnvironmentConfig();
        }

        if (session?.ready && !config?.config?.firebase) {
            configStateActions();
            console.log('🟢 Initialization: Environment config set');
        }
    }, [session?.ready]);

    //? Note: Firebase CMS webContent and webMenu will be requested and stored on first pageload / refresh
    useEffect(() => {
        async function pageStateActions() {
            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webConfig',
            });

            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webContent',
            });

            await actions.cmsSourceFirebaseActions.storeAllData({
                config: siteConfig?.testing?.firebase ?? config.config.firebase,
                docName: 'webMenu',
            });

            await actions.cmsSourceFirebaseActions.setLoaded(true);

            const webConfigData = getCMSObject('data', 'webConfig') ?? {};
            const siteConfigWithOutFunction = JSON.parse(JSON.stringify(siteConfig));
            const siteConfigFromCMS = JSON.parse(webConfigData?.siteConfig ?? '{}') ?? {};
            const siteConfigData = { ...siteConfigWithOutFunction, ...siteConfigFromCMS };
            delete webConfigData.siteConfig;
            delete siteConfigData.testing;

            // Set config store
            await actions.configActions.setWebConfig(webConfigData);
            await actions.configActions.setSiteConfig(siteConfigData);
            await actions.configActions.setLoaded(true);

            scrollToHashElement();
        }

        if (session?.ready && config?.config?.firebase && !cmsSourceFirebase?.loaded) {
            pageStateActions();
            console.log('🟢 Initialization: CMS Data and Config Stores synced');
        }
    }, [session?.ready, config]);

    useEffect(() => {
        async function playerStateActions() {
            await actions.userActions.getPlayerData();
            scrollToHashElement();
        }

        if (session?.ready) {
            playerStateActions();
            console.log('🟢 Initialization: Player validation completed');
        }
    }, [user?.loggedIn, session?.ready]);

    // Get GTM ID based on the environment from the config api
    const gtmId = config?.config?.gtm_id;
    if (gtmId) {
        const tagManagerArgs = {
            gtmId,
        };
        TagManager.initialize(tagManagerArgs);
    }

    //? Redirect players to login page if the page is logged in only page and the players are not logged in
    if (pageData?.loggedInOnly && !user.loggedIn) {
        return <LoggedInOnlyPages />;
    }

    const isLoaded = session?.ready && config?.loaded && cmsSourceFirebase.loaded;

    if (!isLoaded) {
        return <LoadingIndicator priority='critical' />;
    }

    return (
        <FeatureLockout config={config} page={pageData} user={user} options={{ redirectPath: pageData?.featureDisabledRedirectPath }}>
            <Helmet defaultTitle='Whitelabel Lottery' titleTemplate='%s | Whitelabel Lottery'>
                <title>{pageData?.title}</title>
            </Helmet>
            <IfNotMobile>
                <CMSContent localStorageObject='webContent' contentPath='data.webNotices' className='fixed-top' noticePlacement='web-notice-top-fixed' />
                <CMSContent localStorageObject='webContent' contentPath='data.webNotices' className='' noticePlacement='web-notice-top' />
                <Header />
                <CMSContent localStorageObject='webContent' contentPath='data.webNotices' className='' noticePlacement='web-notice-below-header' />
            </IfNotMobile>
            <div className={`content-root ${pageData?.className}`}>
                <ErrorBoundary fallback={<TeleScript line={errorBoundaryFallback} />} details={{ pageData, user, config }}>
                    <div className={`${pageData?.className}-container`}>
                        <div className='galaxy-fold-open-your-device d-none'></div>

                        <UserLockout currentSection={pageData?.className} disableRedirect={pageData?.className}>
                            <DisplayContent note='Show Home Hero Banner section' isVisible={config?.web?.features?.homeHeroBanner}>
                                <HeroBanner
                                    banners={banners}
                                    bannerName={config?.site?.banners?.homeCarousel ?? ''}
                                    actions={actions}
                                    allowedPaths={config?.site?.allowedBannerPaths ?? ['/', '/home']}
                                    handleBannerActions={false}
                                />
                            </DisplayContent>

                            {pageData?.className !== 'account-confirm' && (
                                <PointsDashboard
                                    user={user}
                                    config={config}
                                    actions={actions}
                                    telescript={telescriptPointsDashboard}
                                    cmsSourceFirebase={cmsSourceFirebase}
                                    Menu={Menu}
                                />
                            )}

                            <div className='layout container'>
                                <div className='main'>
                                    <div className='page-content w-100'>
                                        <DisplayContent note='Show page related Title Breadcrumbs' isVisible={config?.web?.features?.titleBreadcrumbs}>
                                            {path && <TitleBreadcrumb pageData={pageData} />}
                                        </DisplayContent>

                                        {children}
                                    </div>
                                </div>
                            </div>
                        </UserLockout>

                        <RenewExpiredSession />
                        <RefreshPageForGuests />
                        <IdleTimeout />
                    </div>
                </ErrorBoundary>
            </div>
            <IfNotMobile>
                <Footer />
                <CMSContent localStorageObject='webContent' contentPath='data.webNotices' className='fixed-bottom' noticePlacement='web-notice-bottom-fixed' />
            </IfNotMobile>
            <StyleOverrides cmsSourceFirebase={cmsSourceFirebase} />
        </FeatureLockout>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Layout);
